import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types';

// Extended User type that includes the Firestore document ID
interface UserWithId extends User {
  id: string;
}

interface UserManagementProps {
  onBack: () => void;
}

const AdminUserManagement: React.FC<UserManagementProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<UserWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [cleanupEmail, setCleanupEmail] = useState<string>('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [verificationRequests, setVerificationRequests] = useState<any[]>([]);
  const [verificationRequestsLoading, setVerificationRequestsLoading] = useState(false);
  const [manualVerifyEmail, setManualVerifyEmail] = useState<string>('');
  const [manualVerifyLoading, setManualVerifyLoading] = useState(false);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [currentUser]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading users for admin...');

      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as UserWithId));

      console.log('Loaded users:', usersData.length);
      console.log('Admin users:', usersData.filter(user => user.role === 'admin'));
      setUsers(usersData);
    } catch (error: any) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'student' | 'teacher' | 'admin') => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole
      });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      // Security: Removed user ID exposure in logs
    } catch (error: any) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role.');
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone and will delete both their account and authentication.`)) {
      return;
    }

    try {
      // First, clean up all links before deleting the user
      await cleanupUserLinks(userId);
      
      // Delete the user from Firebase Auth using Cloud Function
      const deleteUserCompletely = httpsCallable(functions, 'deleteUserCompletely');
      await deleteUserCompletely({ userId });
      
      // Then delete the user document from Firestore
      await deleteDoc(doc(db, 'users', userId));
      setUsers(users.filter(user => user.id !== userId));
      // Security: Removed user ID exposure in logs
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user completely. Please try again.');
    }
  };

  const cleanupUserLinks = async (userId: string) => {
    try {
      // Get all users to clean up links
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const updatePromises: Promise<void>[] = [];

      allUsersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const updates: any = {};

        // Remove userId from linkedStudents arrays (for teachers)
        if (userData.linkedStudents && userData.linkedStudents.includes(userId)) {
          updates.linkedStudents = userData.linkedStudents.filter((id: string) => id !== userId);
        }

        // Remove userId from linkedBuddies arrays (for students)
        if (userData.linkedBuddies && userData.linkedBuddies.includes(userId)) {
          updates.linkedBuddies = userData.linkedBuddies.filter((id: string) => id !== userId);
        }

        // Remove userId from linkedTeachers arrays (for students)
        if (userData.linkedTeachers && userData.linkedTeachers.includes(userId)) {
          updates.linkedTeachers = userData.linkedTeachers.filter((id: string) => id !== userId);
        }

        // Only update if there are changes
        if (Object.keys(updates).length > 0) {
          updatePromises.push(updateDoc(doc(db, 'users', userDoc.id), updates));
        }
      });

      // Execute all updates
      await Promise.all(updatePromises);
      // Security: Removed user ID exposure in logs
    } catch (error) {
      console.error('Error cleaning up user links:', error);
      throw error; // Re-throw to be caught by handleDeleteUser
    }
  };

  const handleCleanupOrphanedAccount = async () => {
    if (!cleanupEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in as admin to use this tool');
      return;
    }

    try {
      setCleanupLoading(true);
      setError('');
      
      // Attempting to cleanup account
      
      // Use HTTP function instead of callable function
      const response = await fetch('https://us-central1-music-lesson-notetaker.cloudfunctions.net/cleanupOrphanedAuthAccountHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: cleanupEmail.trim(),
          adminEmail: currentUser.email
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to cleanup account');
      }
      
      console.log('Cleanup result:', result);
      setError(''); // Clear any previous errors
      setCleanupEmail(''); // Clear the input
      
      // Show success message
      alert(result.message || 'Account cleanup completed successfully');
    } catch (error: any) {
      console.error('Error cleaning up orphaned account:', error);
      setError(`Failed to clean up orphaned account: ${error.message || 'Unknown error'}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  const loadVerificationRequests = async () => {
    try {
      setVerificationRequestsLoading(true);
      const requestsSnapshot = await getDocs(collection(db, 'verificationRequests'));
      const requests = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by creation date, newest first
      requests.sort((a: any, b: any) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });
      
      setVerificationRequests(requests);
    } catch (error) {
      console.error('Error loading verification requests:', error);
      setError('Failed to load verification requests');
    } finally {
      setVerificationRequestsLoading(false);
    }
  };

  const handleManualVerification = async (email: string) => {
    try {
      setManualVerifyLoading(true);
      setError('');

      const manualEmailVerification = httpsCallable(functions, 'manualEmailVerification');
      const result = await manualEmailVerification({ email });
      
      if ((result.data as any).success) {
        alert(`Account for ${email} has been manually verified!`);
        setManualVerifyEmail('');
        loadUsers(); // Refresh user list
        loadVerificationRequests(); // Refresh verification requests
      } else {
        setError((result.data as any).message || 'Failed to manually verify account');
      }
    } catch (error: any) {
      console.error('Manual verification error:', error);
      setError(error.message || 'Failed to manually verify account');
    } finally {
      setManualVerifyLoading(false);
    }
  };

  const handleFixEmailVerification = async () => {
    if (!cleanupEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in as admin to use this tool');
      return;
    }

    try {
      setCleanupLoading(true);
      setError('');
      
      // Attempting to fix email verification
      
      // Find the user by email (now that we store email in Firestore)
      const userQuery = query(
        collection(db, 'users'),
        where('email', '==', cleanupEmail.trim())
      );
      const querySnapshot = await getDocs(userQuery);
      
      if (querySnapshot.empty) {
        setError(`No user found with email: ${cleanupEmail.trim()}. Note: This tool only works for users who signed up after the email field was added to Firestore. For older users, please find them in the user table above and manually update their emailVerified field.`);
        return;
      }
      
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      // Update the emailVerified field in Firestore
      await updateDoc(doc(db, 'users', userDoc.id), {
        emailVerified: true
      });
      
      // Successfully fixed email verification
      setError(''); // Clear any previous errors
      setCleanupEmail(''); // Clear the input
      
      // Show success message
      alert(`Email verification has been fixed for ${cleanupEmail.trim()}. They should now be able to login without being asked to verify their email.`);
      
    } catch (error: any) {
      console.error('Error fixing email verification:', error);
      setError(`Failed to fix email verification: ${error.message || 'Unknown error'}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupOrphanedBuddies = async () => {
    if (!currentUser) {
      setError('You must be logged in as admin to use this tool');
      return;
    }

    try {
      setCleanupLoading(true);
      setError('');
      
      console.log('Starting cleanup of orphaned buddy references...');
      
      // Get all users
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const allUserIds = new Set(allUsersSnapshot.docs.map(doc => doc.id));
      
      let totalCleaned = 0;
      const updatePromises: Promise<void>[] = [];
      
      // Check each user's linkedBuddies array
      for (const userDoc of allUsersSnapshot.docs) {
        const userData = userDoc.data();
        const linkedBuddies = userData.linkedBuddies || [];
        
        // Filter out buddy IDs that no longer exist
        const validBuddies = linkedBuddies.filter((buddyId: string) => allUserIds.has(buddyId));
        
        // If we removed any invalid buddies, update the user document
        if (validBuddies.length !== linkedBuddies.length) {
          const removedCount = linkedBuddies.length - validBuddies.length;
          // User had orphaned buddy references
          
          updatePromises.push(
            updateDoc(doc(db, 'users', userDoc.id), {
              linkedBuddies: validBuddies
            })
          );
          
          totalCleaned += removedCount;
        }
      }
      
      // Execute all updates
      await Promise.all(updatePromises);
      
      console.log(`Cleanup complete. Removed ${totalCleaned} orphaned buddy references.`);
      setError(''); // Clear any previous errors
      
      // Show success message
      alert(`Cleanup complete! Removed ${totalCleaned} orphaned buddy references from ${updatePromises.length} users.`);
      
    } catch (error: any) {
      console.error('Error cleaning up orphaned buddy references:', error);
      setError(`Failed to clean up orphaned buddy references: ${error.message || 'Unknown error'}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <h2 className="text-xl font-bold">Access Denied</h2>
            <p>You must be an admin to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">User Management</h1>
              <p className="text-blue-100">Manage users, roles, and permissions</p>
            </div>
            <button
              onClick={onBack}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <p>{error}</p>
            <button
              onClick={() => setError('')}
              className="mt-2 text-red-200 hover:text-white underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Cleanup Orphaned Accounts Section */}
        <div className="bg-yellow-600 text-white p-4 rounded-lg mb-6">
          <h3 className="text-lg font-bold mb-2">🔧 Cleanup Orphaned Accounts</h3>
          <p className="text-yellow-100 mb-3">
            If a user can't sign up because their email is "already in use" but they can't login because there's "no user document", 
            use this tool to clean up their orphaned Firebase Auth account.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter email address to cleanup"
              value={cleanupEmail}
              onChange={(e) => setCleanupEmail(e.target.value)}
              className="flex-1 px-3 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-md text-white placeholder-yellow-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
            <button
              onClick={handleCleanupOrphanedAccount}
              disabled={cleanupLoading || !cleanupEmail.trim()}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 disabled:bg-opacity-10 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              {cleanupLoading ? 'Cleaning...' : 'Cleanup Account'}
            </button>
          </div>
        </div>

        {/* Fix Email Verification Section */}
        <div className="bg-green-600 text-white p-4 rounded-lg mb-6">
          <h3 className="text-lg font-bold mb-2">📧 Fix Email Verification Loop</h3>
          <p className="text-green-100 mb-3">
            If a user is stuck in an email verification loop (asked to verify every time they login), 
            use this tool to manually mark their email as verified.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter email address to fix verification"
              value={cleanupEmail}
              onChange={(e) => setCleanupEmail(e.target.value)}
              className="flex-1 px-3 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-md text-white placeholder-green-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
            <button
              onClick={handleFixEmailVerification}
              disabled={cleanupLoading || !cleanupEmail.trim()}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 disabled:bg-opacity-10 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              {cleanupLoading ? 'Fixing...' : 'Fix Verification'}
            </button>
          </div>
        </div>

        {/* Clean Up Orphaned Buddy References Section */}
        <div className="bg-purple-600 text-white p-4 rounded-lg mb-6">
          <h3 className="text-lg font-bold mb-2">🧹 Clean Up Orphaned Buddy References</h3>
          <p className="text-purple-100 mb-3">
            Remove references to deleted users from other users' linkedBuddies arrays. 
            This fixes "Missing or insufficient permissions" errors in the Social component.
          </p>
          <button
            onClick={handleCleanupOrphanedBuddies}
            disabled={cleanupLoading}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 disabled:bg-opacity-10 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md transition-all duration-200"
          >
            {cleanupLoading ? 'Cleaning...' : 'Clean Up Orphaned References'}
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading users...</p>
          </div>
        )}

        {/* Users Table */}
        {!loading && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">All Users ({users.length})</h2>
                <button
                  onClick={loadUsers}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-all duration-200"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Instrument
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-750">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                              {user.name?.charAt(0) || '?'}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {user.name || 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-400">
                              {user.nickname || 'No nickname'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {user.email || 'No email'}
                        {user.id === currentUser?.uid && (
                          <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                            (Current User)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value as 'student' | 'teacher' | 'admin')}
                          className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="student">Student</option>
                          <option value="teacher">Teacher</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {user.instrument || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDeleteUser(user.id, user.name || 'Unknown')}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200"
                          disabled={user.role === 'admin' && user.id === currentUser.uid}
                          title={user.role === 'admin' && user.id === currentUser.uid ? "Cannot delete your own admin account" : "Delete user"}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <p>No users found.</p>
              </div>
            )}
          </div>
        )}

        {/* Manual Verification Section */}
        <div className="bg-blue-600 text-white p-4 rounded-lg mb-6">
          <h3 className="text-lg font-bold mb-2">🔧 Manual Email Verification</h3>
          <p className="text-blue-100 mb-3">
            Manually verify an email address when Firebase verification is stuck or broken.
          </p>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Email Address</label>
              <input
                type="email"
                value={manualVerifyEmail}
                onChange={(e) => setManualVerifyEmail(e.target.value)}
                className="w-full px-3 py-2 bg-white text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="user@example.com"
              />
            </div>
            <button
              onClick={() => handleManualVerification(manualVerifyEmail)}
              disabled={!manualVerifyEmail.trim() || manualVerifyLoading}
              className="bg-white text-blue-600 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {manualVerifyLoading ? 'Verifying...' : 'Manually Verify'}
            </button>
          </div>
        </div>

        {/* Verification Requests Section */}
        <div className="bg-green-600 text-white p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold">📧 Verification Requests</h3>
            <button
              onClick={loadVerificationRequests}
              disabled={verificationRequestsLoading}
              className="bg-white text-green-600 px-4 py-2 rounded-md hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              {verificationRequestsLoading ? 'Loading...' : 'Refresh Requests'}
            </button>
          </div>
          <p className="text-green-100 mb-4">
            Users who have requested manual verification due to Firebase verification issues.
          </p>
          
          {verificationRequests.length > 0 ? (
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="space-y-3">
                {verificationRequests.map((request) => (
                  <div key={request.id} className="bg-white bg-opacity-10 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{request.email}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            request.status === 'pending' ? 'bg-yellow-500 text-yellow-900' :
                            request.status === 'completed' ? 'bg-green-500 text-green-900' :
                            'bg-red-500 text-red-900'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-green-100 mb-2">{request.reason}</p>
                        <p className="text-xs text-green-200">
                          Requested: {request.createdAt ? new Date((request as any).createdAt.toDate()).toLocaleString() : 'Unknown'}
                        </p>
                      </div>
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleManualVerification(request.email)}
                          disabled={manualVerifyLoading}
                          className="ml-4 bg-white text-green-600 px-3 py-1 rounded text-sm hover:bg-green-50 transition-colors disabled:opacity-50"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white bg-opacity-10 rounded-lg p-4 text-center">
              <p className="text-green-100">No verification requests found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;
